// Global API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081/api/v1',
  TIMEOUT: 10000, // 10 seconds
  RETRY_ATTEMPTS: 3,
} as const;

// API Endpoints
export const API_ENDPOINTS = {
  AUTH: {
    AUTHENTICATE: '/auth/users/authenticate',
    REFRESH: '/auth/refresh',
    VALIDATE: '/auth/validate',
    LOGOUT: '/auth/logout',
  },
  COMMON: {
    COUNTRIES: '/common/countries',
    PORTS: '/common/ports',
  },
  DASHBOARD: {
    DATA: '/dashboard',
    GENERAL: (branchId?: number) => branchId ? `/dashboard/general?branchId=${branchId}` : '/dashboard/general',
    PLANT_HEAD: (branchId?: number, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return `/dashboard/plant-head${queryString ? `?${queryString}` : ''}`;
    },
    EXECUTIVE: (startDate?: string, endDate?: string, branchId?: number) => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (branchId) params.append('branchId', branchId.toString());
      const queryString = params.toString();
      return `/dashboard/executive${queryString ? `?${queryString}` : ''}`;
    },
  },
  RM_DASHBOARD: {
    DATA: (branchId?: number) =>
      branchId
        ? `/reports/raw-material-dashboard?branchId=${branchId}`
        : '/reports/raw-material-dashboard',
    AGED_INVENTORY: (branchId?: number, months?: number) => {
      const params = new URLSearchParams();
      if (branchId != null) params.append('branchId', String(branchId));
      if (months != null) params.append('months', String(months));
      const qs = params.toString();
      return `/reports/raw-material-dashboard/aged-inventory${qs ? `?${qs}` : ''}`;
    },
  },
  FINISHED_GOODS_DASHBOARD: {
    SUMMARY: (branchId?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      return `/inventory/finished-goods/summary${params.toString() ? `?${params.toString()}` : ''}`;
    },
    ALL: (branchId?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      return `/inventory/finished-goods/all${params.toString() ? `?${params.toString()}` : ''}`;
    },
    BY_STATUS: (branchId?: number, status: string = 'PACKED') => {
      const params = new URLSearchParams();
      params.append('status', status);
      if (branchId) params.append('branchId', branchId.toString());
      return `/inventory/finished-goods/by-status?${params.toString()}`;
    },
    TOP_BY_VALUE: (branchId?: number, limit: number = 10) => {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      if (branchId) params.append('branchId', branchId.toString());
      return `/inventory/finished-goods/top/by-value?${params.toString()}`;
    },
    TOP_BY_QUANTITY: (branchId?: number, limit: number = 10) => {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      if (branchId) params.append('branchId', branchId.toString());
      return `/inventory/finished-goods/top/by-quantity?${params.toString()}`;
    },
    BY_CATEGORY: (branchId?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      return `/inventory/finished-goods/by-category${params.toString() ? `?${params.toString()}` : ''}`;
    },
    VIEW: '/inventory/finished-goods/view',
    VIEW_EXTENDED: (branchId?: number, categoryId?: number, endgoodId?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (categoryId) params.append('categoryId', categoryId.toString());
      if (endgoodId) params.append('endgoodId', endgoodId.toString());
      return `/inventory/finished-goods/view/extended${params.toString() ? `?${params.toString()}` : ''}`;
    },
    AGING_SUMMARY: (branchId?: number, categoryId?: number, inventoryType?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (categoryId) params.append('categoryId', categoryId.toString());
      if (inventoryType) params.append('inventoryType', inventoryType);
      return `/inventory/finished-goods/aging/summary${params.toString() ? `?${params.toString()}` : ''}`;
    },
    AGING_DETAILS: (
      branchId?: number,
      categoryId?: number,
      inventoryType?: string,
      agingBucket?: string,
      endgoodId?: number
    ) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (categoryId) params.append('categoryId', categoryId.toString());
      if (inventoryType) params.append('inventoryType', inventoryType);
      if (agingBucket) params.append('agingBucket', agingBucket);
      if (endgoodId) params.append('endgoodId', endgoodId.toString());
      return `/inventory/finished-goods/aging/details${params.toString() ? `?${params.toString()}` : ''}`;
    },
    LOW_STOCK_ALERTS: (branchId?: number, threshold?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (threshold !== undefined) params.append('threshold', threshold.toString());
      return `/inventory/finished-goods/alerts/low-stock${params.toString() ? `?${params.toString()}` : ''}`;
    },
  },
  CUSTOMERS: {
    LIST: '/sales/customers/all',
    DETAIL: (id: string) => `/sales/customers/${id}`,
    CREATE: '/sales/customers',
    UPDATE: (id: string) => `/sales/customers/${id}`,
    DELETE: (id: string) => `/sales/customers/${id}`,
    SEARCH: '/sales/customers/search',
    STATES: '/sales/states',
    // Additional endpoints
    ACTIVE: '/sales/customers/active',
    BY_STATUS: (status: string) => `/sales/customers/status/${status}`,
    VALIDATE: '/sales/customers/validate',
    DUPLICATE_CHECK: '/sales/customers/duplicate-check',
    EXPORT: '/sales/customers/export',
  },
  // Add other endpoints as needed
  SUPPLIERS: {
    LIST: '/purchase/suppliers',
    DETAIL: (id: string) => `/purchase/suppliers/${id}`,
    CREATE: '/purchase/suppliers',
    UPDATE: (id: string) => `/purchase/suppliers/${id}`,
    DELETE: (id: string) => `/purchase/suppliers/${id}`,
    SEARCH: '/purchase/suppliers/search',
  },
  ENDGOODS: {
    LIST: '/inventory/endgoods',
    DETAIL: (id: string) => `/inventory/endgoods/${id}`,
    CREATE: '/inventory/endgoods',
    UPDATE: (id: string) => `/inventory/endgoods/${id}`,
    DELETE: (id: string) => `/inventory/endgoods/${id}`,
    ID_NAME: '/inventory/endgoods/id-name',
    ID_NAME_CATEGORY: '/inventory/endgoods/id-name-category',
    BY_CATEGORY: (categoryId: string) => `/inventory/endgoods/category/${categoryId}`,
    COST: '/inventory/endgoods/cost',
  },
  ENDGOOD_COST_MASTER: {
    /** POST body = dto; branchId required query param */
    CREATE: (branchId: number) => `/inventory/endgood-cost-master?branchId=${branchId}`,
    DETAIL: (id: string, branchId: number) => `/inventory/endgood-cost-master/${id}?branchId=${branchId}`,
    BY_ENDGOOD: (endgoodId: string, branchId: number) =>
      `/inventory/endgood-cost-master/endgood/${endgoodId}?branchId=${branchId}`,
    AS_OF: (endgoodId: string, branchId: number, asOfDate: string) => {
      const params = new URLSearchParams({ branchId: String(branchId), asOfDate });
      return `/inventory/endgood-cost-master/endgood/${endgoodId}/as-of?${params.toString()}`;
    },
    UPDATE: (id: string, branchId: number) => `/inventory/endgood-cost-master/${id}?branchId=${branchId}`,
    DELETE: (id: string, branchId: number) => `/inventory/endgood-cost-master/${id}?branchId=${branchId}`,
  },
  SALES_ORDERS: {
    LIST: '/sales/orders',
    DETAIL: (id: string) => `/sales/orders/${id}/summary`,
    CREATE: '/sales/orders',
    UPDATE: (id: string) => `/sales/orders/${id}`,
    CLOSE: (id: string, updatedBy?: number) => {
      const params = new URLSearchParams();
      if (updatedBy != null) params.append('updatedBy', String(updatedBy));
      const query = params.toString();
      return `/sales/orders/${id}/close${query ? `?${query}` : ''}`;
    },
    DELETE: (id: string) => `/sales/orders/${id}`,
    SEARCH: '/sales/orders/search',
    // Order Detail endpoints - updated to match Swagger specification
    ITEMS: (soId: string) => `/sales/orders/${soId}/details`,
    CREATE_ITEM: (orderId: string) => `/sales/orders/${orderId}/details`,
    UPDATE_ITEM: (detailId: string) => `/sales/orders/details/${detailId}`,
    DELETE_ITEM: (detailId: string) => `/sales/orders/details/${detailId}`,
    BATCH_CREATE_DETAILS: (orderId: string) => `/sales/orders/${orderId}/details/batch`,
    BATCH_UPDATE_DETAILS: (orderId: string) => `/sales/orders/${orderId}/details/batch`,
    // Additional endpoints from Swagger
    WITH_DETAILS: (id: string) => `/sales/orders/${id}/with-details`,
    BY_STATUS: (status: string) => `/sales/orders/status/${status}`,
    BY_CUSTOMER: (customerId: string) => `/sales/orders/customer/${customerId}`,
    BY_BRANCH_STATUS: (branchId: string, status: string) => `/sales/orders/branch/${branchId}/status/${status}`,
    BY_BRANCH_CUSTOMER: (branchId: string, customerId: string) => `/sales/orders/branch/${branchId}/customer/${customerId}`,
    BY_BRANCH_COUNT: (branchId: string) => `/sales/orders/branch/${branchId}/count`,
    CALCULATE_TOTALS: (orderId: string) => `/sales/orders/${orderId}/calculate-totals`,
    CALCULATE_ALL_TOTALS: '/sales/orders/calculate-all-totals',
    // Alternative endpoints that might be used
    // ORDER_ITEMS: (soId: string) => `/sales/orders/${soId}/order-items`,
    // ORDER_DETAILS: (soId: string) => `/sales/orders/${soId}/order-details`,
    // ADD_ORDER_DETAIL: '/sales/order-details',
    // // Additional common endpoints
    // UPDATE_STATUS: (id: string) => `/sales/orders/${id}/status`,
    // APPROVE: (id: string) => `/sales/orders/${id}/approve`,
    // REJECT: (id: string) => `/sales/orders/${id}/reject`,
    // CANCEL: (id: string) => `/sales/orders/${id}/cancel`,
    // CLONE: (id: string) => `/sales/orders/${id}/clone`,
    // EXPORT: (id: string) => `/sales/orders/${id}/export`,
    // PRINT: (id: string) => `/sales/orders/${id}/print`,
  },
  PURCHASE_ORDERS: {
    LIST: '/purchase/orders',
    DETAIL: (id: string) => `/purchase/orders/${id}`,
    CREATE: '/purchase/orders',
    UPDATE: (id: string) => `/purchase/orders/${id}`,
    UPDATE_STATUS: (id: string, status: string) => `/purchase/orders/${id}/status?status=${encodeURIComponent(status)}`,
    DELETE: (id: string) => `/purchase/orders/${id}`,
    PO_NUMBERS: '/purchase/orders/po-numbers',
    APPROVED_PO_NUMBERS: '/purchase/orders/po-numbers/approved',
    STATIC_TERMS: '/purchase/orders/static-terms',

    ITEMS: (poId: string) => `/purchase/order-details/purchase-order/${poId}`,
    ITEM_DETAIL: (id: string) => `/purchase/order-details/${id}`,
    CREATE_ITEM: '/purchase/order-details',
    UPDATE_ITEM: (id: string) => `/purchase/order-details/${id}`,
    DELETE_ITEM: (id: string) => `/purchase/order-details/${id}`,
    SEARCH: '/purchase/orders/search',
    LAST_TRANSACTIONS: (rawItemId: string, branchId?: number) => {
      const baseUrl = `/purchase/order-details/raw-item/${rawItemId}/last-transactions`;
      if (branchId) {
        return `${baseUrl}?branchId=${branchId}`;
      }
      return baseUrl;
    },
    LAST_TRANSACTIONS_BATCH: '/purchase/order-details/raw-items/last-transactions',

  },
  CHALLANS: {
    LIST: '/challan',
    DETAIL: (id: string) => `/challan/${id}`,
    CREATE: '/challan',
    UPDATE: (id: string) => `/challan/${id}`,
    DELETE: (id: string) => `/challan/${id}`,
    SEARCH: '/challan/search',
    DETAILS: (challanId: string) => `/challan-details/challan/${challanId}`,
    CREATE_DETAIL: '/challan-details/challan',
    CREATE_DETAILS_BATCH: '/challan-details/batch',
    UPDATE_DETAIL: (id: string) => `/challan-details/challan/${id}`,
    DELETE_DETAIL: (id: string) => `/challan-details/challan/${id}`
  },
  BRANCHES: {
    LIST: '/inventory/branches',
    DETAIL: (id: string) => `/inventory/branches/${id}`,
    CREATE: '/inventory/branches',
    UPDATE: (id: string) => `/inventory/branches/${id}`,
    DELETE: (id: string) => `/inventory/branches/${id}`,
  },
  /** Branch LUT effective row (align path with backend BranchLutController base mapping). */
  BRANCH_LUT: {
    EFFECTIVE: (branchId: number, date?: string) => {
      const params = new URLSearchParams({ branchId: String(branchId) });
      if (date) params.append('date', date);
      return `/inventory/branch-lut/effective?${params.toString()}`;
    },
  },
  RAW_ITEMS: {
    GET_ALL: '/inventory/raw-items/all',
    ID_NAME: '/inventory/raw-items/id-name',
    ID_NAME_RAW_BOP: '/inventory/raw-items/id-name/raw-bop',
    GET_BY_ID: (id: string) => `/inventory/raw-items/${id}`,
    CREATE: '/inventory/raw-items',
    UPDATE: (id: string) => `/inventory/raw-items/${id}`,
    DELETE: (id: string) => `/inventory/raw-items/${id}`,
    SEARCH: '/inventory/raw-items/search',
    SCAN: '/inventory/raw-items/scan',
    QR_PAYLOAD: '/inventory/raw-items/qr-payload',
    // Additional endpoints
    BY_CATEGORY: (categoryId: string) => `/inventory/raw-items/category/${categoryId}`,
    STOCK_INFO: (id: string) => `/inventory/raw-items/${id}/stock`,
    PRICE_HISTORY: (id: string) => `/inventory/raw-items/${id}/price-history`,
  },
  RAW_ITEM_VARIANTS: {
    GET_BY_RAW_ITEM: (rawItemId: string) => `/inventory/raw-item-variants/raw-item/${rawItemId}`,
    DELETE_BY_RAW_ITEM: (rawItemId: string) => `/inventory/raw-item-variants/raw-item/${rawItemId}`,
    CREATE_MULTIPLE: (rawItemId: string) => `/inventory/raw-item-variants/raw-item/${rawItemId}/create-multiple-variants`,
  },
  ENDGOOD_VARIANTS: {
    GET_BY_ENDGOOD: (endgoodId: string) => `/inventory/endgood-variants/endgood/${endgoodId}`,
    DELETE_BY_ENDGOOD: (endgoodId: string) => `/inventory/endgood-variants/endgood/${endgoodId}`,
    CREATE_MULTIPLE: (endgoodId: string) => `/inventory/endgood-variants/endgood/${endgoodId}/create-multiple-variants`,
  },
  CATEGORIES: {
    LIST: '/inventory/item-categories/all',
    DETAIL: (id: string) => `/inventory/item-categories/${id}`,
    CREATE: '/inventory/item-categories',
    UPDATE: (id: string) => `/inventory/item-categories/${id}`,
    DELETE: (id: string) => `/inventory/categories/${id}`,
    // Additional endpoints
    ACTIVE: '/inventory/item-categories/active',
    BY_TYPE: (type: string) => `/inventory/item-categories/type/${type}`,
  },
  ENDGOOD_CATEGORIES: {
    LIST: '/inventory/endgood-categories/all',
    DETAIL: (id: string) => `/inventory/endgood-categories/${id}`,
    CREATE: '/inventory/endgood-categories',
    UPDATE: (id: string) => `/inventory/endgood-categories/${id}`,
    DELETE: (id: string) => `/inventory/endgood-categories/${id}`,
  },
  HSN_CODES: {
    LIST: '/inventory/hsn/all',
    DETAIL: (id: string) => `/inventory/hsn/${id}`,
    CREATE: '/inventory/hsn',
    UPDATE: (id: string) => `/inventory/hsn/${id}`,
    DELETE: (id: string) => `/inventory/hsn/${id}`,
  },
  TAX_RATE_HISTORY: {
    CREATE: '/inventory/tax-rate-history',
    BY_HSN_ID: (hsnId: number) => `/inventory/tax-rate-history/hsn-id/${hsnId}`,
    BY_HSN_CODE: (hsnCode: string) => `/inventory/tax-rate-history/hsn/${hsnCode}`,
    DETAIL: (id: string) => `/inventory/tax-rate-history/${id}`,
    APPLICABLE_RATE: (hsnId: number, date: string) => `/inventory/tax-rate-history/applicable-rate?hsnId=${hsnId}&date=${date}`,
  },
  PROCESSES: {
    LIST: '/production/processes/all',
    DETAIL: (id: string) => `/production/processes/${id}`,
    CREATE: '/production/processes',
    UPDATE: (id: string) => `/production/processes/${id}`,
    DELETE: (id: string) => `/production/processes/${id}`,
    SEARCH: '/production/processes/search',
  },
  PROCESS_FLOWS: {
    LIST: '/production/process-flows/all',
    DETAIL: (id: string) => `/production/process-flows/${id}`,
    CREATE: '/production/process-flows',
    UPDATE: (id: string) => `/production/process-flows/${id}`,
    DELETE: (id: string) => `/production/process-flows/${id}`,
    SEARCH: '/production/process-flows/search',
  },
  PROCESS_STEPS: {
    LIST: '/production/process-steps',
    DETAIL: (id: string) => `/production/process-steps/${id}`,
    CREATE: '/production/process-steps',
    UPDATE: (id: string) => `/production/process-steps/${id}`,
    DELETE: (id: string) => `/production/process-steps/${id}`,
    SEARCH: '/production/process-steps/search',
    BY_PROCESS_FLOW: (processFlowId: number) => `/production/process-steps/process-flow/${processFlowId}`,
  },
  BOM_HEADERS: {
    LIST: '/production/bom/header',
    DETAIL: (id: string) => `/production/bom/header/${id}`,
    BOM_DETAIL: (bomHeaderId: string) => `/production/bom/detail/bom-header/${bomHeaderId}`,
    CREATE: '/production/bom/header',
    UPDATE: (id: string) => `/production/bom/header/${id}`,
    DELETE: (id: string) => `/production/bom/header/${id}`,
    SEARCH: '/production/bom/header/search',
    BY_ENDGOOD: (endgoodId: string) => `/production/bom/header/endgood/${endgoodId}`,
    USAGE: (rawItemId: string) => `/production/bom/raw-item-usage/raw-item/${rawItemId}`,
  },
  BOM_DETAILS: {
    LIST: '/production/bom/detail',
    DETAIL: (id: string) => `/production/bom/detail/${id}`,
    CREATE: '/production/bom/detail',
    UPDATE: (id: string) => `/production/bom/detail/${id}`,
    DELETE: (id: string) => `/production/bom/detail/${id}`,
    SEARCH: '/production/bom/detail/search',
  },
  ENDGOOD_PROCESS_WEIGHT: {
    BY_ENDGOOD: (endgoodId: string, processFlowId?: string | number) =>
      `/production/endgood-process-weights/endgood/${endgoodId}${
        processFlowId != null && String(processFlowId).trim() !== ''
          ? `?processFlowId=${encodeURIComponent(String(processFlowId))}`
          : ''
      }`,
    MISSING_WITH_PRODUCTION: (startDate: string, endDate: string, branchId?: number) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      if (branchId != null) params.append('branchId', String(branchId));
      return `/production/endgood-process-weights/missing-with-production?${params.toString()}`;
    },
    CREATE: '/production/endgood-process-weights',
    UPDATE: (id: string) => `/production/endgood-process-weights/${id}`,
    DELETE: (id: string) => `/production/endgood-process-weights/${id}`,
  },
  GRN: {
    LIST: '/purchase/grn',
    DETAIL: (id: string) => `/purchase/grn/${id}`,
    GRN_DETAIL: (grnId: string) => `/purchase/grn-details/grn/${grnId}`,
    CREATE: '/purchase/grn',
    UPDATE: (id: string) => `/purchase/grn/${id}`,
    DELETE: (id: string) => `/purchase/grn/${id}`,
    SEARCH: '/purchase/grn/search',
    APPROVE: (id: string) => `/purchase/grn/${id}/approve`,
    EXPORT: (branchId?: number, status?: string, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (status && status !== 'all') params.append('status', status);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return `/purchase/grn/export${queryString ? `?${queryString}` : ''}`;
    },
  },
  GRN_DETAILS: {
    LIST: '/purchase/grn-details',
    DETAIL: (id: string) => `/purchase/grn-details/${id}`,
    CREATE: '/purchase/grn-details',
    UPDATE: (id: string) => `/purchase/grn-details/${id}`,
    DELETE: (id: string) => `/purchase/grn-details/${id}`,
    SEARCH: '/purchase/grn-details/search',
    BY_RAWITEM: (rawItemId: string) => `/purchase/grn-details/raw-item/${rawItemId}/consumption`, // Get GRN details by raw item ID
    BY_RAWITEM_BATCH: '/purchase/grn-details/raw-items/consumption', // Batch get GRN consumption for multiple raw items
    REMAINING_QUANTITY: (heatNumber: string) => `/purchase/grn/heat-numbers/${heatNumber}/available-quantity`,
  },
  WORK_ORDERS: {
    LIST: '/production/work-orders/all/minimal',
    LIST_OPTIMIZED: '/production/work-orders/all/optimized',
    DETAIL: (id: string) => `/production/work-orders/summary/${id}`,
    CREATE: '/production/work-orders',
    UPDATE: (id: string) => `/production/work-orders/${id}`,
    DELETE: (id: string) => `/production/work-orders/${id}`,
    SEARCH: '/production/work-orders/search',
    // Additional endpoints
    BY_STATUS: (status: string) => `/production/work-orders/status/${status}`,
    BY_PRIORITY: (priority: string) => `/production/work-orders/priority/${priority}`,
    BY_BRANCH: (branchId: string) => `/production/work-orders/branch/${branchId}`,
    BY_CUSTOMER: (customerId: string) => `/production/work-orders/customer/${customerId}`,
    BY_SALES_ORDER: (salesOrderId: string) => `/production/work-orders/sales-order/${salesOrderId}`,
    BY_ENDGOOD: (endgoodId: string) => `/production/work-orders/endgood/${endgoodId}`,
    UPDATE_STATUS: (id: string) => `/production/work-orders/${id}/status`,
    UPDATE_PRIORITY: (id: string) => `/production/work-orders/${id}/priority`,
    ASSIGN: (id: string) => `/production/work-orders/${id}/assign`,
    COMPLETE: (id: string) => `/production/work-orders/${id}/complete`,
    CANCEL: (id: string) => `/production/work-orders/${id}/cancel`,
    GET_STATS: '/production/work-orders/stats',
    GET_REPORTS: '/production/work-orders/reports',
    EXPORT: '/production/work-orders/export',
  },
  WORK_ORDER_OPERATIONS: {
    LIST: '/production/work-order-operations',
    DETAIL: (id: string) => `/production/work-order-operations/${id}`,
    CREATE: '/production/work-order-operations',
    UPDATE: (id: string) => `/production/work-order-operations/${id}`,
    DELETE: (id: string) => `/production/work-order-operations/${id}`,
    SEARCH: '/production/work-order-operations/search',
    BULK_REGISTER: '/production/work-order-operations/bulk',
    // Additional endpoints
    BY_WORK_ORDER: (workOrderId: string) => `/production/work-order-operations/work-order/${workOrderId}`,
    BY_STATUS: (status: string) => `/production/work-order-operations/status/${status}`,
    BY_WORK_CENTER: (workCenter: string) => `/production/work-order-operations/work-center/${workCenter}`,
    BY_MACHINE: (machineCode: string) => `/production/work-order-operations/machine/${machineCode}`,
    BY_OPERATOR: (operator: string) => `/production/work-order-operations/operator/${operator}`,
    BY_PROCESS_STEP: (processStepId: string) => `/production/work-order-operations/process-step/${processStepId}`,
    UPDATE_STATUS: (id: string) => `/production/work-order-operations/${id}/status`,
    START_OPERATION: (id: string) => `/production/work-order-operations/${id}/start`,
    COMPLETE_OPERATION: (id: string) => `/production/work-order-operations/${id}/complete`,
    PAUSE_OPERATION: (id: string) => `/production/work-order-operations/${id}/pause`,
    RESUME_OPERATION: (id: string) => `/production/work-order-operations/${id}/resume`,
    QUALITY_CHECK: (id: string) => `/production/work-order-operations/${id}/quality-check`,
    GET_STATS: '/production/work-order-operations/stats',
    GET_REPORTS: '/production/work-order-operations/reports',
    EXPORT: '/production/work-order-operations/export',
  },
  WORK_ORDER_VARIANT_BREAKDOWNS: {
    LIST: '/production/work-order-variant-breakdowns',
    DETAIL: (id: string) => `/production/work-order-variant-breakdowns/${id}`,
    CREATE: '/production/work-order-variant-breakdowns',
    CREATE_BULK: '/production/work-order-variant-breakdowns/bulk',
    UPDATE: (id: string) => `/production/work-order-variant-breakdowns/${id}`,
    DELETE: (id: string) => `/production/work-order-variant-breakdowns/${id}`,
    SEARCH: '/production/work-order-variant-breakdowns/search',
    // Additional endpoints
    BY_WORK_ORDER: (workOrderId: string) => `/production/work-order-variant-breakdowns/work-order/${workOrderId}`,
    BY_ENDGOOD_VARIANT: (endgoodVariantId: string) => `/production/work-order-variant-breakdowns/endgood-variant/${endgoodVariantId}`,
    BY_ENDGOOD: (endgoodId: string) => `/production/work-order-variant-breakdowns/by-endgood/${endgoodId}`,
    BY_STATUS: (isActive: string) => `/production/work-order-variant-breakdowns/status/${isActive}`,
    GET_STATS: '/production/work-order-variant-breakdowns/stats',
    GET_REPORTS: '/production/work-order-variant-breakdowns/reports',
    EXPORT: '/production/work-order-variant-breakdowns/export',
  },
  MATERIAL_ISSUES: {
    LIST: '/inventory/material-issues',
    DETAIL: (id: string) => `/inventory/material-issues/${id}`,
    CREATE: '/inventory/material-issues',
    CREATE_BULK: '/inventory/material-issues/bulk',
    UPDATE: (id: string) => `/inventory/material-issues/${id}`,
    DELETE: (id: string) => `/inventory/material-issues/${id}`,
    SEARCH: '/inventory/material-issues/search',
    // Additional endpoints
    BY_WORK_ORDER: (workOrderId: string) => `/inventory/material-issues/work-order/${workOrderId}`,
    BY_WORK_ORDER_OPERATION: (workOrderOperationId: string) => `/inventory/material-issues/work-order-operation/${workOrderOperationId}`,
    BY_RAW_ITEM: (rawItemId: string) => `/inventory/material-issues/raw-item/${rawItemId}`,
    BY_BOM_DETAIL: (bomDetailId: string) => `/inventory/material-issues/bom-detail/${bomDetailId}`,
    BY_STATUS: (status: string) => `/inventory/material-issues/status/${status}`,
    BY_ISSUE_TYPE: (issueType: string) => `/inventory/material-issues/issue-type/${issueType}`,
    BY_BRANCH: (branchId: string) => `/inventory/material-issues/branch/${branchId}`,
    BY_DATE_RANGE: '/inventory/material-issues/date-range',
    UPDATE_STATUS: (id: string) => `/inventory/material-issues/${id}/status`,
    RETURN_MATERIAL: (id: string) => `/inventory/material-issues/${id}/return`,
    CONSUME_MATERIAL: (id: string) => `/inventory/material-issues/${id}/consume`,
    REPORT_WASTAGE: (id: string) => `/inventory/material-issues/${id}/wastage`,
    GET_STATS: '/inventory/material-issues/stats',
    GET_REPORTS: '/inventory/material-issues/reports',
    EXPORT: '/inventory/material-issues/export',
  },
  WIP_TRACKING: {
    LIST: (branchId: string) => `/production/work-orders/wip-summary/branch/${branchId}`,
    SEARCH: '/production/work-orders/wip-summary/search',
    FILTER: '/production/work-orders/wip-summary/filter',
    BY_STATUS: (status: string) => `/production/work-orders/wip-summary/status/${status}`,
    BY_PRIORITY: (priority: string) => `/production/wip-summary/priority/${priority}`,
    BY_DEPARTMENT: (department: string) => `/production/work-orders/wip-summary/department/${department}`,
    BY_ASSIGNED_TO: (assignedTo: string) => `/production/work-orders/wip-summary/assigned-to/${assignedTo}`,
    BY_CUSTOMER: (customerName: string) => `/production/work-orders/wip-summary/customer/${customerName}`,
    BY_DATE_RANGE: '/production/wip-summary/date-range',
    OVERDUE: '/production/wip-summary/overdue',
    READY_FOR_PRODUCTION: '/production/wip-summary/ready-for-production',
    MATERIAL_ISSUED: '/production/wip-summary/material-issued',
    EXPORT: '/production/wip-summary/export',
    STATS: '/production/wip-summary/stats',
  },
  PACKING_ENTRIES: {
    LIST: '/packing-headers',
    DETAIL: (id: string) => `/packing-headers/${id}`,
    CREATE: '/packing-headers',
    CREATE_BULK: '/packing-headers/bulk',
    UPDATE: (id: string) => `/packing-headers/${id}`,
    DELETE: (id: string) => `/packing-headers/${id}`,
    SEARCH: '/packing-details/search',
    DELETE_BATCH: (batchId: number) => `/packing-headers/batch/${batchId}`,
    BY_BATCH: (batchId: string | number, branchId?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      return `/packing-headers/batch/${batchId}${params.toString() ? `?${params.toString()}` : ''}`;
    },
    BY_WORK_ORDER: (workOrderId: string) => `/packing-headers/work-order/${workOrderId}`,
    BY_HEADER_ID: (headerId: string) => `/packing-details/packing-header/${headerId}`,
    BY_BATCH_DETAILS: (batchId: string | number) => `/packing-details/batch/${batchId}`,
    UPDATE_PACKING_DETAIL: (id: string | number) => `/packing-details/${id}`,
    MARK_AS_INVOICED: '/packing-details/mark-as-invoiced',
    MARK_AS_INVOICED_BY_BOX_NUMBERS: '/packing-details/mark-as-invoiced-by-box-numbers',
    BY_PROCESS_STEP: (processStepId: string) => `/production/packing-entries/process-step/${processStepId}`,
    BY_STATUS: (status: string) => `/production/packing-entries/status/${status}`,
    BY_DATE_RANGE: '/production/packing-entries/date-range',
    EXPORT: '/production/packing-entries/export',
    GET_STATS: '/production/packing-entries/stats',
    COMPLETED_WORK_ORDER_UNPACKED: (branchId: number) => `/packing-headers/completed-work-orders/unpacked/branch/${branchId}`,
    COMPLETED_WORK_ORDER_UNPACKED_BY_ID: (workOrderId: number) => `/packing-headers/completed-work-orders/unpacked/${workOrderId}`,
    ENDGOODS_WITH_AVAILABLE_BOXES: '/packing/endgoods/with-available-boxes',
    ENDGOOD_AVAILABLE_BOXES: (endgoodId: string) => `/packing/endgoods/${endgoodId}/available-boxes`,
    /** Get all boxes for endgood: available + boxes linked to invoice detail when invoiceDetailId provided (edit invoice) */
    ENDGOOD_BOXES: (endgoodId: string) => `/packing/endgoods/${endgoodId}/boxes`,

    // Box Type endpoints
    BOX_TYPES: '/box-types/all',
    BOX_TYPE_BY_ID: (id: string) => `/box-types/${id}`,
    CREATE_BOX_TYPE: '/box-types',
    UPDATE_BOX_TYPE: (id: string) => `/box-types/${id}`,
    DELETE_BOX_TYPE: (id: string) => `/box-types/${id}`,
    SEARCH_BOX_TYPES: '/box-types/search',
    EXPORT_BOX_TYPES: '/box-types/export',
    
    // Endgood Box Type endpoints
    ENDGOOD_BOX_TYPES: '/endgood-box-types',
    ENDGOOD_BOX_TYPE_BY_ID: (id: string) => `/endgood-box-types/${id}`,
    CREATE_ENDOOD_BOX_TYPE: '/endgood-box-types',
    UPDATE_ENDOOD_BOX_TYPE: (id: string) => `/endgood-box-types/${id}`,
    DELETE_ENDOOD_BOX_TYPE: (id: string) => `/endgood-box-types/${id}`,
    GET_ENDOOD_BOX_TYPES_BY_ENDOOD_ID: (endgoodId: string) => `/endgood-box-types/endgood/${endgoodId}`,
    SEARCH_ENDOOD_BOX_TYPES: '/endgood-box-types/search',
    EXPORT_ENDOOD_BOX_TYPES: '/endgood-box-types/export',
  },
  INVOICES: {
    LIST: '/invoice/invoice-headers',
    REFERENCE_LIST: (branchId?: number) => {
      const params = new URLSearchParams();
      params.append('status', 'E_INVOICED');
      if (branchId != null) params.append('branchId', branchId.toString());
      return `/invoice/invoice-headers/reference-invoices?${params.toString()}`;
    },
    DETAIL: (id: string) => `/invoice/invoice-headers/${id}`,
    CREATE: '/invoice/invoice-headers',
    UPDATE: (id: string) => `/invoice/invoice-headers/${id}`,
    DELETE: (id: string) => `/invoice/invoice-headers/${id}`,
    SEARCH: '/invoice/invoice-headers/search',
    BY_STATUS: (status: string) => `/invoice/invoice-headers/status/${status}`,
    BY_CUSTOMER: (customerId: string) => `/invoice/invoice-headers/customer/${customerId}`,
    BY_DATE_RANGE: '/invoice/invoice-headers/date-range',
    EXPORT: '/invoice/invoice-headers/export',
    GET_STATS: '/invoice/invoice-headers/stats',
    GENERATE_EINVOICE: (id: string, ewayBillRequired: boolean = true) =>
      `/invoice/invoice-headers/${id}/generate-einvoice?eway-bill-required=${ewayBillRequired}`,
    GENERATE_EWAY_BILL: (id: string) => `/invoice/invoice-headers/${id}/generate-eway-bill`,
    CANCEL_EINVOICE: (id: string) => `/invoice/invoice-headers/${id}/cancel-einvoice`,

    // Invoice Details endpoints
    DETAILS_BY_HEADER: (headerId: string) => `/invoice/invoice-details/header/${headerId}`,
    CREATE_DETAIL: '/invoice/invoice-details',
    UPDATE_DETAIL: (id: string) => `/invoice/invoice-details/${id}`,
    DELETE_DETAIL: (id: string) => `/invoice/invoice-details/${id}`,
    
    // Other Charges endpoints
    OTHER_CHARGES_BY_INVOICE: (invoiceId: string) => `/invoices//other-charges/invoice/${invoiceId}`,
    CREATE_OTHER_CHARGE: '/invoice/other-charges',
    UPDATE_OTHER_CHARGE: (id: string) => `/invoice/other-charges/${id}`,
    DELETE_OTHER_CHARGE: (id: string) => `/invoice/other-charges/${id}`,
    
    // Charge Master endpoints
    CHARGE_MASTERS: '/invoice/charge-masters',
    CHARGE_MASTER_BY_ID: (id: string) => `/invoice/charge-masters/${id}`,
    CREATE_CHARGE_MASTER: '/invoice/charge-masters',
    UPDATE_CHARGE_MASTER: (id: string) => `/invoice/charge-masters/${id}`,
    DELETE_CHARGE_MASTER: (id: string) => `/invoice/charge-masters/${id}`,
    SEARCH_CHARGE_MASTERS: '/invoice/charge-masters/search',
    EXPORT_CHARGE_MASTERS: '/charge-masters/export',

    /** Transporter master (invoice module) */
    TRANSPORTER_MASTERS: '/invoice/transporter-masters',
    TRANSPORTER_MASTERS_SEARCH: '/invoice/transporter-masters/search',
  },
  CREDIT_DEBIT_NOTES: {
    LIST: '/invoice/credit-debit-notes',
    DETAIL: (id: string) => `/invoice/credit-debit-notes/${id}`,
    CREATE: '/invoice/credit-debit-notes',
    UPDATE: (id: string) => `/invoice/credit-debit-notes/${id}`,
    GENERATE_EINVOICE: (id: string, ewayBillRequired: boolean = true) =>
      `/invoice/credit-debit-notes/${id}/generate-einvoice?eway-bill-required=${ewayBillRequired}`,
    CANCEL_EINVOICE: (id: string) => `/invoice/credit-debit-notes/${id}/cancel-einvoice`,
    ITEMS_BY_HEADER: (headerId: string) => `/invoice/credit-debit-notes/${headerId}/items`,
    SAVE_ITEMS: (headerId: string) => `/invoice/credit-debit-notes/${headerId}/items`,
  },
  // Utility endpoints for Sales Order Management
  SALES_UTILS: {
    GENERATE_ORDER_NUMBER: '/sales/orders/generate-number',
    VALIDATE_ORDER: '/sales/orders/validate',
    CHECK_AVAILABILITY: '/sales/orders/check-availability',
    CALCULATE_TOTALS: '/sales/orders/calculate-totals',
    GET_ORDER_HISTORY: (customerId: string) => `/sales/orders/customer/${customerId}/history`,
    GET_ORDER_STATS: '/sales/orders/stats',
    GET_ORDER_REPORTS: '/sales/orders/reports',
  },
  
  // Inventory utilities
  INVENTORY_UTILS: {
    CHECK_STOCK: '/inventory/stock/check',
    GET_STOCK_LEVELS: '/inventory/stock/levels',
    UPDATE_STOCK: '/inventory/stock/update',
    GET_STOCK_ALERTS: '/inventory/stock/alerts',
  },
  
  // Inventory Reports
  INVENTORY_REPORTS: {
    HEAT_NUMBER_CONSUMPTION: (branchId: number) => `/reports/inventory/heat-number-consumption/branch/${branchId}`,
    HEAT_NUMBER_CONSUMPTION_BY_RAW_ITEM: (branchId: number, rawItemId: number) =>
      `/reports/inventory/heat-number-consumption/branch/${branchId}/raw-item/${rawItemId}`,
    MATERIAL_ISSUE: (branchId: number) => `/reports/material-issue/branch/${branchId}`,
    INVENTORY_AS_OF_DATE: () => `/reports/inventory/as-of-date`,
    STORE_IN_OUT_DETAILS: `/reports/store-in-out/details`,
    STEEL_CONSUMPTION: {
      CONSUMPTION_DETAIL: '/reports/steel-consumption/consumption/detail',
      CONSUMPTION_SUMMARY: '/reports/steel-consumption/consumption/summary',
      YIELD_DETAIL: '/reports/steel-consumption/yield/detail',
      YIELD_SUMMARY: '/reports/steel-consumption/yield/summary',
    },
    AGING_ANALYSIS: (branchId?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      const queryString = params.toString();
      return `/reports/inventory/aging-analysis${queryString ? `?${queryString}` : ''}`;
    },
    AGING_ANALYSIS_DETAILS: (branchId?: number, agingBucket?: string) => {
      const params = new URLSearchParams();
      if (branchId != null) params.append('branchId', branchId.toString());
      if (agingBucket) params.append('agingBucket', agingBucket);
      const queryString = params.toString();
      return `/reports/inventory/aging-analysis/details${queryString ? `?${queryString}` : ''}`;
    },
  },
  
  // Supplier Reports
  SUPPLIER_REPORTS: {
    PERFORMANCE: (branchId?: number, year?: number, month?: number, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (year) params.append('year', year.toString());
      if (month) params.append('month', month.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return `/reports/supplier-performance${queryString ? `?${queryString}` : ''}`;
    },
    SUPPLIER_BALANCE: (branchId: number) => `/reports/supplier-performance/supplier-balance/branch/${branchId}`,
    DETAILS: (supplierId: number, branchId?: number, year?: number, month?: number) => {
      const params = new URLSearchParams();
      params.append('supplierId', supplierId.toString());
      if (branchId) params.append('branchId', branchId.toString());
      if (year) params.append('year', year.toString());
      if (month) params.append('month', month.toString());
      return `/reports/supplier-performance/details?${params.toString()}`;
    },
    LAST_12_MONTHS: (branchId: number, supplierId?: number) => {
      const params = new URLSearchParams();
      params.append('branchId', branchId.toString());
      if (supplierId != null) params.append('supplierId', supplierId.toString());
      return `/reports/supplier-performance/last-12-months?${params.toString()}`;
    },
  },
  
  // Sales Order Reports
  SALES_ORDER_REPORTS: {
    PERFORMANCE: (branchId?: number, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return `/reports/sales-order-performance${queryString ? `?${queryString}` : ''}`;
    },
    DETAIL: (salesOrderId: number) => `/reports/sales-order-performance/sales-order/${salesOrderId}`,
  },

  // Invoice Reports
  INVOICE_REPORTS: {
    SALES: (branchId?: number, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return `/reports/sales${queryString ? `?${queryString}` : ''}`;
    },
    CREDIT_DEBIT: (branchId?: number, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      // Backend endpoint name assumed to mirror sales report naming.
      return `/reports/credit-debit${queryString ? `?${queryString}` : ''}`;
    },
    /** GET /reports/hsn — HSN + UOM aggregates (optional branchId, startDate, endDate) */
    HSN: (branchId?: number, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return `/reports/hsn${queryString ? `?${queryString}` : ''}`;
    },
  },
  
  // MIS Reports
  MIS_REPORTS: {
    BY_BRANCH: (branchId: number) => `/reports/mis/branch/${branchId}`,
    DAILY_SUMMARY_POPULATE: (startDate: string, endDate: string) => 
      `/reports/mis-daily-summary/populate?startDate=${startDate}&endDate=${endDate}`,
    DAILY_BY_BRANCH_AND_DATE_RANGE: (branchId: number, startDate: string, endDate: string) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      return `/reports/mis-daily-summary/branch/${branchId}/date-range?${params.toString()}`;
    },
  },
  
  PRODUCTION_REPORTS: {
    SUMMARY_BY_DATE_RANGE: (branchId: number, startDate: string, endDate: string, searchText?: string) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      if (searchText && searchText.trim()) {
        params.append('searchText', searchText.trim());
      }
      return `/reports/production-summary/branch/${branchId}/date-range?${params.toString()}`;
    },
    /** Summary table grouped by production date, endgood, and process */
    SUMMARY_TABLE_BY_DATE_RANGE: (branchId: number, startDate: string, endDate: string, searchText?: string) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      if (searchText && searchText.trim()) {
        params.append('searchText', searchText.trim());
      }
      return `/reports/production-summary/branch/${branchId}/summary-table?${params.toString()}`;
    },
  },
  /** GET /reports/worker-daily-performance?workerId=&branchId=&startDate=&endDate= */
  WORKER_DAILY_PERFORMANCE: (params: { workerId?: number; branchId?: number; startDate?: string; endDate?: string }) => {
    const q = new URLSearchParams();
    if (params.workerId != null) q.append('workerId', String(params.workerId));
    if (params.branchId != null) q.append('branchId', String(params.branchId));
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    const query = q.toString();
    return `/reports/worker-daily-performance${query ? `?${query}` : ''}`;
  },
  SCRAP_REPORTS: {
    DAILY_SUMMARY_CURRENT_MONTH: (branchId: number) => `/reports/scrap/daily-summary/current-month?branchId=${branchId}`,
    DATE_RANGE: (startDate: string, endDate: string, branchId?: number) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      if (branchId) {
        params.append('branchId', branchId.toString());
      }
      return `/reports/scrap/date-range?${params.toString()}`;
    },
    DETAILS: (productionDate: string, branchId: number) => `/reports/scrap/details?productionDate=${productionDate}&branchId=${branchId}`,
  },
  MATERIAL_PLANNING: {
    BY_RAW_ITEM: (rawItemId: number, branchId?: number, year?: number, month?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (year) params.append('year', year.toString());
      if (month) params.append('month', month.toString());
      const queryString = params.toString();
      return `/reports/material-planning/raw-item/${rawItemId}${queryString ? `?${queryString}` : ''}`;
    },
    ALL: (branchId?: number, year?: number, month?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (year) params.append('year', year.toString());
      if (month) params.append('month', month.toString());
      const queryString = params.toString();
      return `/reports/material-planning${queryString ? `?${queryString}` : ''}`;
    },
  },
  PURCHASE_DASHBOARD: {
    MONTHLY_SUMMARY: (branchId?: number, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return `/reports/purchase-dashboard/monthly-summary${queryString ? `?${queryString}` : ''}`;
    },
    MONTHLY_SUMMARY_DETAILED: (branchId?: number, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return `/reports/purchase-dashboard/monthly-summary-detailed${queryString ? `?${queryString}` : ''}`;
    },
    CATEGORY_DETAILS: (year: number, month: number, branchId?: number) => {
      const params = new URLSearchParams();
      params.append('year', year.toString());
      params.append('month', month.toString());
      if (branchId) params.append('branchId', branchId.toString());
      return `/reports/purchase-dashboard/category-details?${params.toString()}`;
    },
    SUPPLIER_DETAILS: (year: number, month: number, branchId?: number) => {
      const params = new URLSearchParams();
      params.append('year', year.toString());
      params.append('month', month.toString());
      if (branchId) params.append('branchId', branchId.toString());
      return `/reports/purchase-dashboard/supplier-details?${params.toString()}`;
    },
  },
  SALES_DASHBOARD: {
    BY_CATEGORY: (branchId?: number, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return `/reports/sales-dashboard/by-category${queryString ? `?${queryString}` : ''}`;
    },
    BY_CUSTOMER: (branchId?: number, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return `/reports/sales-dashboard/by-customer${queryString ? `?${queryString}` : ''}`;
    },
    TOP_PRODUCTS: (branchId?: number, startDate?: string, endDate?: string, limit: number = 10) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('limit', String(limit));
      const queryString = params.toString();
      return `/reports/sales-dashboard/top-products${queryString ? `?${queryString}` : ''}`;
    },
    PRODUCT_ACTIVITY: (branchId?: number, days: number = 30) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      params.append('days', String(days));
      const queryString = params.toString();
      return `/reports/sales-dashboard/product-activity${queryString ? `?${queryString}` : ''}`;
    },
  },
  RAW_ITEM_RATE_HISTORY: {
    BY_RAW_ITEM: (rawItemId: number, supplierId?: number, startDate?: string, endDate?: string) => {
      const params = new URLSearchParams();
      if (supplierId) params.append('supplierId', supplierId.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return `/reports/raw-item-rate-history/raw-item/${rawItemId}${queryString ? `?${queryString}` : ''}`;
    },
  },
  

  
  USERS: {
    LIST: '/auth/users',
    DETAIL: (id: string) => `/auth/users/${id}`,
    CREATE: '/auth/users',
    UPDATE: (id: string) => `/auth/users/${id}`,
    DELETE: (id: string) => `/auth/users/${id}`,
    PROFILE: '/auth/users/profile',
    PROFILE_BY_ID: (id: string) => `/auth/users/${id}/profile`,
    PROFILE_BY_USERNAME: (username: string) => `/auth/users/profile/username/${username}`,
    UPDATE_PROFILE: (id: string) => `/auth/users/${id}/profile`,
    WITH_ROLES_AND_BRANCHES: (id: string) => `/auth/users/${id}/with-roles-and-branches`,
    CHANGE_PASSWORD: (id: string) => `/auth/users/${id}/change-password`,
    SEARCH: '/auth/users/search',
  },
  ROLES: {
    LIST: '/auth/roles',
    DETAIL: (id: string) => `/auth/roles/${id}`,
    CREATE: '/auth/roles',
    UPDATE: (id: string) => `/auth/roles/${id}`,
    DELETE: (id: string) => `/auth/roles/${id}`,
    SEARCH: '/auth/roles/search',
    // Additional endpoints for role permissions
    WITH_PERMISSIONS: (id: string) => `/auth/roles/${id}/with-permissions`,
    PERMISSIONS: (id: string) => `/auth/roles/${id}/permissions`,
  },
  FILES: {
    // File upload endpoints (upload first, then attach)
    UPLOAD: '/inventory/files/upload',
    PRESIGNED_URL: '/inventory/files/presigned-upload',
    CONFIRM_UPLOAD: '/inventory/files/confirm-upload',
    CLEANUP_TEMP: '/inventory/files/cleanup-temp',
    DOWNLOAD: (filePath: string) => `/inventory/files/download/${filePath}`,
    DOWNLOAD_FILE: '/inventory/files/download-file', // Download with fileKey query parameter
    
    // Endgood file management endpoints (backend APIs)
    ATTACH_SINGLE: (endgoodId: string, fileId: string) => `/inventory/endgoods/${endgoodId}/files/${fileId}`,
    ATTACH_BULK: (endgoodId: string) => `/inventory/endgoods/${endgoodId}/files/bulk`,
    REMOVE: (endgoodId: string, fileId: string) => `/inventory/endgoods/${endgoodId}/files/${fileId}`,
    GET_BY_ENDGOOD: (endgoodId: string) => `/inventory/endgoods/${endgoodId}/files`,
    
    // Legacy endpoints (if still needed)
    DELETE: (fileId: string) => `/inventory/files/${fileId}`,
    LIST: '/inventory/files',
    DETAIL: (fileId: string) => `/inventory/files/${fileId}`,
  },
  WORKERS: {
    LIST: (branchId?: number) =>
      branchId != null
        ? `/inventory/workers/summary/by-branch/${branchId}/active`
        : '/inventory/workers',
    DETAIL: (id: string) => `/inventory/workers/${id}`,
    CREATE: '/inventory/workers',
    UPDATE: (id: string) => `/inventory/workers/${id}`,
    DELETE: (id: string) => `/inventory/workers/${id}`,
    SEARCH: '/inventory/workers/search',
    GENERATE_CODE: '/inventory/workers/generate-code',
    BY_WORK_AREA_ID: (workAreaId: string, branchId?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      return `/inventory/workers/by-work-area/${workAreaId}${params.toString() ? `?${params.toString()}` : ''}`;
    },
  },
  DEPARTMENTS: {
    BY_BRANCH_ACTIVE: (branchId: number | string) => `/inventory/departments/by-branch/${branchId}/active`,
  },
  WORK_AREAS: {
    ACTIVE: '/inventory/work-areas/active',
  },
  PHYSICAL_MACHINE: {
    LIST: (branchId?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      return `/production/physical-machines${params.toString() ? `?${params.toString()}` : ''}`;
    },
    DETAIL: (id: string) => `/production/physical-machines/${id}`,
    CREATE: '/production/physical-machines',
    UPDATE: (id: string) => `/production/physical-machines/${id}`,
    DELETE: (id: string) => `/production/physical-machines/${id}`,
    SEARCH: '/production/physical-machines/search',
    GENERATE_CODE: '/production/physical-machines/generate-code',
    BY_WORK_AREA_ID: (workAreaId: string, branchId?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId.toString());
      return `/production/physical-machines/by-work-area/${workAreaId}${params.toString() ? `?${params.toString()}` : ''}`;
    },
  },
  STORES: {
    LIST: '/store',
    DETAIL: (id: string) => `/store/${id}`,
    CREATE: '/store',
    UPDATE: (id: string) => `/store/${id}`,
    DELETE: (id: string) => `/store/${id}`,
    SEARCH: '/store/search',
    BY_BRANCH: (branchId: string) => `/store/branch/${branchId}`,
    BY_TYPE: (type: string) => `/store/type/${type}`,
  },
  STORE_ITEMS: {
    LIST: '/store/item',
    DETAIL: (id: string) => `/store/item/${id}`,
    CREATE: '/store/items',
    UPDATE: (id: string) => `/store/items/${id}`,
    DELETE: (id: string) => `/store/items/${id}`,
    SEARCH: '/store/items/search',
    BY_STORE: (storeId: string) => `/store/item/store/${storeId}`,
    BY_ITEM: (itemId: string) => `/store/items/item/${itemId}`,
    INVENTORY: (storeId: string) => `/store/items/inventory/${storeId}`,
  },
  STORE_TRANSACTIONS: {
    LIST: '/store/transaction',
    DETAIL: (id: string) => `/store/transaction/${id}`,
    CREATE: '/store/transaction',
    CREATE_WITH_STOCK: '/store/transaction/with-stock',
    UPDATE: (id: string) => `/store/transaction/${id}`,
    DELETE: (id: string) => `/store/transaction/${id}`,
    SEARCH: '/store/transaction/search',
    BY_STORE: (storeId: string) => `/store/transaction/store/${storeId}`,
    BY_ITEM: (itemId: string) => `/store/transaction/item/${itemId}`,
  },
  STORE_TRANSFERS: {
    LIST: '/store/transfers',
    DETAIL: (id: string) => `/store/transfers/${id}`,
    CREATE: '/store/transfers',
    UPDATE: (id: string) => `/store/transfers/${id}`,
    DELETE: (id: string) => `/store/transfers/${id}`,
    SEARCH: '/store/transfers/search',
    BY_FROM_STORE: (storeId: string) => `/store/transfers/from/${storeId}`,
    BY_TO_STORE: (storeId: string) => `/store/transfers/to/${storeId}`,
    APPROVE: (id: string) => `/store/transfers/${id}/approve`,
    COMPLETE: (id: string) => `/store/transfers/${id}/complete`,
    CANCEL: (id: string) => `/store/transfers/${id}/cancel`,
  },
  RM_REJECTION_REGISTER: {
    LIST: '/inventory/rm-rejection-register',
    DETAIL: (id: number) => `/inventory/rm-rejection-register/${id}`,
    CREATE: '/inventory/rm-rejection-register',
    UPDATE: (id: number) => `/inventory/rm-rejection-register/${id}`,
    DELETE: (id: number) => `/inventory/rm-rejection-register/${id}`,
    BY_BRANCH: (branchId: number) => `/inventory/rm-rejection-register/branch/${branchId}`,
    BY_RAW_ITEM: (rawItemId: number) => `/inventory/rm-rejection-register/raw-item/${rawItemId}`,
    BY_HEAT_NO: (heatNo: string) => `/inventory/rm-rejection-register/heat-no/${heatNo}`,
    BY_DATE_RANGE: (startDate: string, endDate: string) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      return `/inventory/rm-rejection-register/date-range?${params.toString()}`;
    },
    BY_BRANCH_DATE_RANGE: (branchId: number, startDate: string, endDate: string) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      return `/inventory/rm-rejection-register/branch/${branchId}/date-range?${params.toString()}`;
    },
    BY_RAW_ITEM_DATE_RANGE: (rawItemId: number, startDate: string, endDate: string) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      return `/inventory/rm-rejection-register/raw-item/${rawItemId}/date-range?${params.toString()}`;
    },
  },
  TRACKING: {
    BY_HEAT_NUMBER: (heatNo: string) => `/tracking/by-heat-number?heatNo=${encodeURIComponent(heatNo)}`,
    BY_BOX_NUMBER: (boxNumber: string) => `/tracking/by-box-number?boxNumber=${encodeURIComponent(boxNumber)}`,
    BY_WORK_ORDER_NUMBER: (workOrderNumber: string) => `/tracking/by-work-order-number?workOrderNumber=${encodeURIComponent(workOrderNumber)}`,
    BY_INVOICE_NUMBER: (invoiceNo: string, branchId: number) =>
      `/tracking/by-invoice-number?invoiceNo=${encodeURIComponent(invoiceNo)}&branchId=${branchId}`,
  },

  // Job Work module (Phase 1: Order → Send → Receive)
  JOBWORK: {
    ORDERS: '/jobwork/orders',
    ORDER_BY_ID: (id: number | string) => `/jobwork/orders/${id}`,
    ORDER_APPROVE: (id: number | string) => `/jobwork/orders/${id}/approve`,
    ORDER_RELEASE: (id: number | string) => `/jobwork/orders/${id}/release`,
    ORDER_CLOSE: (id: number | string) => `/jobwork/orders/${id}/close`,
    ORDER_CANCEL: (id: number | string) => `/jobwork/orders/${id}/cancel`,
    ORDERS_BY_WORK_ORDER: (workOrderId: number | string) => `/jobwork/orders/by-work-order/${workOrderId}`,
    ORDER_RECONCILIATION: (id: number | string) => `/jobwork/orders/${id}/reconciliation`,
    CHALLANS_SEND: '/jobwork/challans/send',
    CHALLAN_SEND_DISPATCH: (id: number | string) => `/jobwork/challans/send/${id}/dispatch`,
    CHALLANS_RECEIVE: '/jobwork/challans/receive',
    CHALLAN_RECEIVE_CONFIRM: (id: number | string) => `/jobwork/challans/receive/${id}/confirm`,
    CHALLAN_BY_ID: (id: number | string) => `/jobwork/challans/${id}`,
    CHALLAN_CANCEL: (id: number | string) => `/jobwork/challans/${id}/cancel`,
    CHALLANS_BY_ORDER: (orderId: number | string) => `/jobwork/challans/by-order/${orderId}`,
    PENDING: '/jobwork/pending',
    /** Guided create: processes for an endgood (BOM process flow) */
    LOOKUP_PROCESSES: '/jobwork/lookup/processes',
    /** Guided create: WIP WOs for endgood + start process */
    LOOKUP_WIP_WORK_ORDERS: '/jobwork/lookup/wip-work-orders',
  },
} as const;

// HTTP Methods
export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
} as const;

// Default headers
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': '*',
  'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
  'Access-Control-Allow-Headers': 'Content-Type, Authorization',
} as const;

// File Upload Folder Paths
export const UPLOAD_FOLDERS = {
  GRN_SUPPLIER_LAB_REPORT: 'po_receive/supplier_lab_report',
  ENDGOOD: 'endgood',
} as const; 